import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import logger from '../utils/logger';

// Function to get deployment details based on chain ID
const getDeploymentDetails = (chainId: string = '8453') => {
    try {
        // In a production environment, this would be imported from a generated file
        // For now, we'll try to fetch it dynamically
        return fetch(`/deployments/deployment-details-${chainId}.json`)
            .then(response => response.json())
            .catch(() => {
                console.warn(`Failed to load deployment details for chain ${chainId}, using default subgraph URL`);
                return null;
            });
    } catch (error) {
        console.warn(`Error loading deployment details: ${error}`);
        return null;
    }
};

// Get the Graph Node URL from environment variables or use default
const getGraphNodeBaseUrl = (chainId: string): string => {
    // Early return if subgraphs are disabled (silent)
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        return '';
    }

    // Always prioritize the environment variable if available
    const envGraphNodeUrl = import.meta.env.VITE_GRAPH_NODE_URL;
    if (envGraphNodeUrl) {
        return envGraphNodeUrl;
    }

    // Fallback for local development (Anvil)
    if (chainId === '31337') {
        // Use the window.__GRAPH_NODE_URL__ if it's been set (for ngrok or local development)
        if (typeof window !== 'undefined' && (window as any).__GRAPH_NODE_URL__) {
            logger.debug('Using Graph Node URL from window.__GRAPH_NODE_URL__:', (window as any).__GRAPH_NODE_URL__);
            return (window as any).__GRAPH_NODE_URL__;
        }
        // Default to localhost for Anvil (no hardcoded ngrok URL)
        logger.warn('⚠️ No Graph Node URL configured for Anvil chain 31337, using localhost:8000');
        return 'http://localhost:8000';
    }

    // For mainnet chains, use Alchemy subgraphs
    return 'https://subgraphs.alchemy.com/api/subgraphs';
};

// Default subgraph URLs for different environments
// Only initialize if subgraphs are enabled
const DEFAULT_SUBGRAPH_URLS: Record<string, string> = {};

// Lazy initialization function for default URLs
const getDefaultSubgraphUrl = (chainId: string): string => {
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        return '';
    }

    if (!DEFAULT_SUBGRAPH_URLS[chainId]) {
        const baseUrl = getGraphNodeBaseUrl(chainId);

        if (chainId === '31337') {
            DEFAULT_SUBGRAPH_URLS[chainId] = `${baseUrl}/subgraphs/name/memer-multisigtimelockcontroller-31337/graphql`;
        } else if (chainId === '8453') {
            DEFAULT_SUBGRAPH_URLS[chainId] = `${baseUrl}/name/alchemy-memer-tracker`;
        } else if (chainId === '84532') {
            DEFAULT_SUBGRAPH_URLS[chainId] = `${baseUrl}/name/alchemy-memer-tracker`;
        }
    }

    return DEFAULT_SUBGRAPH_URLS[chainId] || '';
};

// Create and export the Apollo Client
export const createApolloClient = async (chainId: string = '8453') => {
    // Check if subgraphs are disabled via environment variable
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        logger.info('🚫 Subgraphs disabled via VITE_DISABLE_SUBGRAPHS environment variable');
        logger.info('📋 Returning null client - no subgraph connections will be made');
        return null;
    }

    // Try to get deployment details
    const deploymentDetails = await getDeploymentDetails(chainId);

    // Map subgraph keys from deployment details to expected keys
    let subgraphUrl;

    if (deploymentDetails?.subgraphs) {
        // Check for different possible keys for the timelock tracker
        subgraphUrl = deploymentDetails.subgraphs.timelockTracker?.url ||
            deploymentDetails.subgraphs.multisigtimelockTracker?.url;
    }

    // Fall back to default URLs if no URL found in deployment details
    if (!subgraphUrl) {
        subgraphUrl = getDefaultSubgraphUrl(chainId) || getDefaultSubgraphUrl('31337');
    }

    logger.debug(`Using subgraph URL: ${subgraphUrl}`);

    return new ApolloClient({
        link: new HttpLink({
            uri: subgraphUrl
        }),
        cache: new InMemoryCache(),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-first',
            },
        },
    });
};

// Create a default client for immediate use
// This will be replaced once we have the chain ID
let defaultClient: ApolloClient<any>;

// Lazy initialization function for default client
const getDefaultClient = (): ApolloClient<any> => {
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        logger.info('🚫 Default client: Subgraphs disabled via VITE_DISABLE_SUBGRAPHS');
        // Create a minimal client that won't make any requests
        return new ApolloClient({
            link: new HttpLink({ uri: 'http://localhost:0' }), // Invalid URL to prevent requests
            cache: new InMemoryCache(),
        });
    }

    if (!defaultClient) {
        const defaultUrl = getDefaultSubgraphUrl('31337');
        defaultClient = new ApolloClient({
            link: new HttpLink({
                uri: defaultUrl
            }),
            cache: new InMemoryCache(),
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'network-only',
                    nextFetchPolicy: 'cache-first',
                },
            },
        });

        // Log the default client URL for debugging
        logger.debug(`Default Apollo client using URL: ${defaultUrl}`);
    }

    return defaultClient;
};

// Only export default client if subgraphs are enabled
export default import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true'
    ? new ApolloClient({
        link: new HttpLink({ uri: 'http://localhost:0' }), // Invalid URL to prevent requests
        cache: new InMemoryCache(),
    })
    : getDefaultClient();
